import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { useContext } from 'react';
import { accumulate } from '@olivierpascal/helpers';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IAppRegion } from '~/lib/appSettings';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { usePersistentForm } from '~/components/utils/PersistentForm';
import { TrackedUtmCampaignContext } from '~/components/utils/TrackedUtmCampaign';
import { useFormAutoFocus } from '~/hooks/useFormAutoFocus';
import { useSignUpMutation } from '~/lib/api/modules/Auth/hooks/useSignUpMutation';
import { getAppSettings } from '~/lib/appSettings';
import { SignUpFormMainSection } from './SignUpFormMainSection';

export type ISignUpFormValues = {
  appRegion?: IAppRegion;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  password?: string;
  passwordConfirmation?: string;
  affiliateCode?: string;
  termsOfServiceAgreement?: boolean;
  userLicenceAgreement?: boolean;
};

export type ISignUpFormProps = IContainerProps & {
  onSuccess?: () => void;
  cancelUrl?: string;
  defaultValues?: DefaultValues<ISignUpFormValues>;
};

export const SignUpForm: React.FC<ISignUpFormProps> = (props) => {
  const { sx, onSuccess, cancelUrl, defaultValues } = props;
  const { t } = useTranslation();
  const trackedUtmCampaignContext = useContext(TrackedUtmCampaignContext);
  const signUpMutation = useSignUpMutation();
  const appSettings = getAppSettings();
  const defaultAppRegion = appSettings.defaultRegion;
  const form = usePersistentForm<ISignUpFormValues>({
    defaultValues: accumulate({ appRegion: defaultAppRegion }, defaultValues),
    excludeFields: [
      'password',
      'passwordConfirmation',
      'userLicenceAgreement',
      'termsOfServiceAgreement',
    ],
  });
  useFormAutoFocus({ form, name: 'firstName' });

  const handleSubmit: SubmitHandler<ISignUpFormValues> = (values) => {
    if (signUpMutation.isLoading) {
      return;
    }

    return signUpMutation.mutateAsync(
      {
        region: values.appRegion ?? defaultAppRegion,
        body: {
          email: values.email ?? '',
          password: values.password ?? '',
          profile: {
            firstname: values.firstName,
            lastname: values.lastName,
            phoneNumber: values.phoneNumber,
            affiliateCode: values.affiliateCode,
          },
          utm: trackedUtmCampaignContext,
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.FormBody
        form={form}
        onSubmit={handleSubmit}
        cancelUrl={cancelUrl}
        submitButtonLabel={t('SignUpForm.sign-up')}
        first
      >
        <SignUpFormMainSection form={form} defaultValues={defaultValues} />
      </ContentBlock.FormBody>
    </ContentBlock>
  );
};
